import { Card, CardContent } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Tabs } from "@shared/ui/tabs";
import { CreditCard, Droplets, MessageSquare, LayoutGrid, Receipt, ClipboardList } from "lucide-react";
import AllAccountsTab from "./tabs/AllAccountsTab";
import AllBillsMainTab from "./tabs/all-bills/Landing";
import AllRequestsMainTab from "./tabs/all-requests/Landing";
import { PageHeader } from "@shared/components/PageHeader";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { useConsumerDetail } from "../../shared/hook";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { useConsumerBillDetails } from "../../dashboard/hook";
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import React, { useMemo } from 'react';

export function Landing() {
  const { t } = useTranslation();
  const { remoteUtilityId, remoteConsumerNumber } = getLoginDataFromStorage();

  // Fetch bill data for the billing trend widget
  const { data: consumerBillData } = useConsumerBillDetails({
    remoteUtilityId: remoteUtilityId,
    remoteConsumerNumber: remoteConsumerNumber,
    fetch_last_six_records: "True",
  });  
  const consumerInfo = consumerBillData?.result;
  const billData = consumerInfo?.billData?.[0];  

  const getUnitForService = (service: string) => {
    if (!service) return 'Units';
    const s = service.toLowerCase();
    if (s.includes('electric') || s.includes('power')) return 'kWh';
    if (s.includes('gas')) return 'CCF';
    if (s.includes('water')) return 'Gal';
    return 'Units';
  };

  const activeUsages = useMemo(() => {
    const details = consumerInfo?.consumerMappingData || [];
    return details.map((detail: any) => {
      const current = Number(detail?.meterDetails?.currentReading) || 0;
      const last = Number(detail?.meterDetails?.lastReading) || 0;
      return {
        name: detail?.utilityService,
        consumption: Math.max(0, current - last),
        unit: getUnitForService(detail?.utilityService)
      };
    });
  }, [consumerInfo]);

  const currency =useCurrency();
  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header text */}
      <PageHeader 
        title={t("All Accounts")}
        subtitle={t("Master account overview — manage all linked sub-accounts from one place")}
      />
      
      {/* Blue Header Card */}
      <Card className="bg-[#3b49df] text-white border-0 shadow-sm rounded-xl overflow-hidden">
        <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center font-bold text-xl tracking-wider">
              {consumerInfo?.firstName?.charAt(0)}{consumerInfo?.lastName?.charAt(0)}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="font-bold text-xl">{consumerInfo?.firstName+ " " +consumerInfo?.lastName}</span>
                <Badge variant="secondary" className="bg-white/20 text-white hover:bg-white/30 border-0 font-medium px-2 py-0.5 text-xs rounded-full">
                  {t("Master Account")}
                </Badge>
              </div>
              <p className="text-blue-100 text-sm font-medium">{consumerInfo?.email} </p>
            </div>
          </div>
          <div className="md:text-right">
            <p className="text-blue-100 text-sm font-medium mb-1">{t("Total Outstanding")}</p>
            <p className="font-bold text-3xl">{billData?.billAmount ?? 0}</p>
          </div>
        </CardContent>
      </Card>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <QuickStatsCard
          title={t("Total Balance Due")}
          displayValue={<span className="text-[22px] text-red-600 font-bold">{`${currency} ${billData?.billAmount ?? '0'}`}</span>}
          subtext={`${consumerInfo?.unpaidSubAccountsCount ?? 0} ${t("account(s) unpaid")}`}
          icon={
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-500">
              <CreditCard className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
        <QuickStatsCard
          title={t("Active Sub-Accounts")}
          displayValue={<span className="text-[22px] text-emerald-500 font-bold">{consumerInfo?.subAccountsCount ?? 0}</span>}
          subtext={t("All accounts active")}
          icon={
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
              <Droplets className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
        <QuickStatsCard
          title={t("Across All Accounts")}
          displayValue={
            <TooltipProvider>
              <Tooltip delayDuration={200}>
                <TooltipTrigger className="cursor-help border-b border-dashed border-emerald-400 font-semibold text-emerald-500">
                  <span className="text-[22px] font-bold">{consumerInfo?.totalUsagexs ?? 0}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="p-3 shadow-lg rounded-lg bg-white border border-slate-200">
                  <div className="flex flex-col gap-2 text-sm font-semibold w-40">
                    <div className="text-xs text-slate-400 mb-1">{t("Usage Details")}</div>
                    {activeUsages.map((usage: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-slate-500 font-medium capitalize">{usage.name}</span>
                        <span className="text-slate-900">{usage.consumption} {usage.unit}</span>
                      </div>
                    ))}
                    {activeUsages.length === 0 && (
                      <div className="text-slate-500 text-xs text-center py-1">{t("No meter data")}</div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
          subtext={t("Total Usage")}
          icon={
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
              <Droplets className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
        <QuickStatsCard
          title={t("Pending Complaints")}
          displayValue={<span className="text-[22px] text-orange-400 font-bold">{consumerInfo?.pendingComplaints}</span>}
          subtext={t("Across all accounts")}
          icon={
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-400">
              <MessageSquare className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
      </div>

      {/* Tabs */}
      <div className="mt-2">
        <Tabs
          defaultValue="all-accounts"
          disableUrlSync={true}
          tabsListFullWidth={true}
          tabsListClassName="grid grid-cols-3 w-full"
          tabComponents={{
            "all-accounts": {
              label: t("All Accounts"),
              icon: <LayoutGrid className="w-4 h-4 text-gray-400" />,
              component: <AllAccountsTab />
            },
            "all-bills": {
              label: t("All Bills"),
              icon: <Receipt className="w-4 h-4 text-gray-400" />,
              component: <AllBillsMainTab />
            },
            "all-requests": {
              label: t("All Requests"),
              icon: <ClipboardList className="w-4 h-4 text-gray-400" />,
              component: <AllRequestsMainTab />
            }
          }}
        />
      </div>
    </div>
  );
}

export default Landing;
